"use client";

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSession } from 'next-auth/react';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { useCompetitionContext } from "../providers/CompetitionContext";
import { useTeamContext, TeamProvider } from '../providers/TeamContext';
import { RouteGuard } from './pages/RouteGuard';
import Header from "../components/header"
import SubMenu from "../components/sub-menu"
import Footer from "../components/footer"
import EditTeam from './pages/EditTeam';
import NewTeam from './pages/NewTeam';
import Points from './pages/Points';
// import Team from './pages/Team';
// import Transfers from './pages/Transfers';
import Leagues from './pages/Leagues';
import Calendar from './pages/Calendar';
import Ranking from './pages/Ranking';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Match, Week } from '@repo/types';
import { LoadingSpinner } from '../components/loading-spinner'
import moment from 'moment';
import 'moment/locale/fr';
import { getFantasyRoute } from '../lib/getFantasyRoute';

const isGameEnded = (competitionData: any) =>
  competitionData?.deadlineInfo?.deadlineWeek === null &&
  (competitionData?.matches?.length || 0) > 0;

const getTeamIdFromPathname = (pathname: string) => {
  const match = pathname.match(/^\/fantasy\/(?:edit|points|public)\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

interface WeekSwitchProps {
  competitionData: any;
}

const WeekSwitch: React.FC<WeekSwitchProps> = ({ competitionData }) => {
  const { weekId, setWeekId } = useCompetitionContext();
  const { team, updateForWeek } = useTeamContext();

  const weeks = competitionData.weeks || [];
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const currentPath = location.pathname;

  if (weeks.length === 0) {
    return <div>No weeks available</div>;
  }

  const isRootRoute = currentPath === ('/fantasy') || currentPath === ('/fantasy/');
  const isNewTeamRoute = currentPath.startsWith('/fantasy/new')
  const isEditRoute = currentPath.startsWith('/fantasy/edit/');
  const isPointsRoute = currentPath.startsWith('/fantasy/points/') || currentPath.startsWith('/fantasy/public/');
  const isPublicRoute = currentPath.startsWith('/fantasy/public/');
  const showWeekSwitch = !currentPath.startsWith('/fantasy/leagues') && !currentPath.startsWith('/fantasy/calendar') && !currentPath.startsWith('/fantasy/rankings');
  const isTeamStartingWeek = (team && team.weekId && (team.weekId >= weekId)) || false;
  const deadlineWeek = competitionData.deadlineInfo?.deadlineWeek;
  const hasTeamWeek = typeof team?.weekId === 'number';
  const isLockedOnEditWeek = Boolean(
    isEditRoute &&
    hasTeamWeek &&
    typeof deadlineWeek === 'number' &&
    team.weekId === deadlineWeek &&
    weekId === team.weekId
  );

  useEffect(() => {
    // If we don't have a weekId set yet
    if (weekId === 0) {
      const deadlineInfo = competitionData.deadlineInfo || {};
      const fallbackWeekId = deadlineInfo.rosterEndWeek ?? deadlineInfo.displayWeek ?? weeks[weeks.length - 1]?.weekId ?? 1;
      const defaultWeekId = (isNewTeamRoute)
        ? (deadlineInfo.deadlineWeek ?? fallbackWeekId)
        : (isPointsRoute)
          ? (deadlineInfo.deadlineWeek ? Math.max(deadlineInfo.deadlineWeek - 1, 1) : fallbackWeekId)
          : (isRootRoute && deadlineInfo.deadlineWeek !== null && deadlineInfo.displayWeek < deadlineInfo.deadlineWeek)
            ? deadlineInfo.displayWeek
            : (deadlineInfo.deadlineWeek ?? fallbackWeekId);

      if (typeof defaultWeekId === 'number') {
        setWeekId(defaultWeekId);
      }
      //todo test if needed updateForWeek(newWeekId);
    }
  }, [weekId, isNewTeamRoute, isPointsRoute, isRootRoute, setWeekId, competitionData, weeks]);

  const foundWeekIndex = weeks.findIndex((week: Week) => week.weekId === weekId);
  const currentWeekIndex = foundWeekIndex >= 0 ? foundWeekIndex : 0;
  const currentWeekData = competitionData.matches.filter((m: Match) => m.weekId === weekId);
  const previousDisabled = currentWeekIndex === 0 || isNewTeamRoute || isTeamStartingWeek || isLockedOnEditWeek;
  const nextDisabled = currentWeekIndex === weeks.length - 1 || isNewTeamRoute ||
    (isPublicRoute && competitionData.deadlineInfo?.deadlineWeek !== null && currentWeekIndex+2 >= competitionData.deadlineInfo.deadlineWeek) ||
    isLockedOnEditWeek;
  const currentMatch = currentWeekData[0];

  const formatStageLabel = (feedUrl: string) => {
    const cleaned = feedUrl.replace(/\s+/g, ' ').trim();
    const separatorMatch = cleaned.match(/\s[-–—]\s/);
    if (!separatorMatch) return cleaned;

    const [start, finish] = cleaned.split(separatorMatch[0], 2);
    if (!start || !finish) return cleaned;

    return `${start.trim()} > ${finish.trim()}`;
  };

  const formatStageTypeInFrench = (rawType: string) => {
    const normalized = rawType.trim().toLowerCase();
    if (!normalized) return 'Étape';

    if (normalized.includes('team time')) {
      return 'CLM équipe';
    }
    if (
      normalized.includes('clm') ||
      normalized.includes('itt') ||
      normalized.includes('time trial') ||
      normalized.includes('individual time') ||
      normalized === 'tt' ||
      normalized === 'time'
    ) {
      return 'CLM';
    }
    if (normalized.includes('flat') || normalized.includes('sprint')) {
      return 'Plaine';
    }
    if (normalized.includes('hilly') || normalized.includes('hill') || normalized.includes('vallon')) {
      return 'Accidentée';
    }
    if (normalized.includes('mountain') || normalized.includes('mont')) {
      return 'Montagne';
    }

    return rawType.charAt(0).toUpperCase() + rawType.slice(1);
  };

  const weekStageLabel = currentMatch
    ? (() => {
        const rawType = (currentMatch.type || '').trim();
        const stageType = currentMatch.weekId === 1
          ? 'CLM équipe'
          : formatStageTypeInFrench(rawType);
        return `Étape ${currentMatch.weekId} | ${stageType}`;
      })()
    : 'Aucune étape sélectionnée';

  const weekRouteLabel = currentMatch ? formatStageLabel(currentMatch.feedUrl) : '';
  const weekDeadlineLabel = currentMatch
    ? moment.utc(currentMatch.date).local().format('dd DD/MM [à] HH:mm')
    : '';

  const goToPreviousWeek = () => {
    if (isLockedOnEditWeek) return;
    if (currentWeekIndex > 0) {
      const newWeekId = weeks[currentWeekIndex - 1]!.weekId;
      setWeekId(newWeekId);
      updateForWeek(newWeekId);

      const deadlineWeek = competitionData.deadlineInfo?.deadlineWeek;
      
      // Handle navigation from edit to points:
      // - If game ended (deadlineWeek is null), always go to points
      // - If navigating to a week before deadlineWeek, go to points
      if (isEditRoute && (deadlineWeek === null || newWeekId < deadlineWeek)) {
        navigate(`/fantasy/points/${params.id}`);
      }
    }
  };

  const goToNextWeek = () => {
    if (isLockedOnEditWeek) return;
    if (currentWeekIndex < weeks.length - 1) {
      const newWeekId = weeks[currentWeekIndex + 1]!.weekId;
      setWeekId(newWeekId);
      updateForWeek(newWeekId);

      const deadlineWeek = competitionData.deadlineInfo?.deadlineWeek;
      
      // Handle navigation from points to edit:
      // - Only go to edit if deadlineWeek exists AND newWeekId >= deadlineWeek
      // - If game ended (deadlineWeek is null), stay on points
      if (deadlineWeek !== null && isPointsRoute && !isPublicRoute && newWeekId >= deadlineWeek) {
        navigate(`/fantasy/edit/${params.id}`);
      }
    }
  };

  return (
    showWeekSwitch && (
      <section className="my-3">
        {/* <div className="flex items-center justify-between bg-[rgb(0_61_96)] px-4 py-2 text-white">
          <span className="text-xs font-bold uppercase tracking-wide">Semaine</span>
          <span className="text-xs font-semibold opacity-90">{`#${weekId || currentWeekData[0]?.weekId || '-'}`}</span>
        </div> */}

        <div className="flex items-center justify-between py-1.5">
          <button
            onClick={goToPreviousWeek}
            disabled={previousDisabled}
            aria-label="Previous week"
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#FDC300] text-black transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex-1 px-2 text-center text-white sm:px-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-white/80 sm:text-xs">
              {weekStageLabel}
            </div>
            {currentMatch ? (
              <div className="mt-0.5 flex flex-col gap-0">
                <div className="text-sm font-extrabold leading-tight text-white sm:text-base">
                  {weekRouteLabel}
                </div>
                <div className="text-[10px] font-medium text-white/80 sm:text-xs">
                  {weekDeadlineLabel}
                </div>
              </div>
            ) : null}
          </div>

          <button
            onClick={goToNextWeek}
            disabled={nextDisabled}
            aria-label="Next week"
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#FDC300] text-black transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    )
  );
};

// This component will redirect users with a teamId from /new to their canonical team route.
const RedirectIfTeamExists = ({ teamId, weekId, user, competitionData }: any) => {
  if (isGameEnded(competitionData) && !teamId) {
    return <Navigate to="/fantasy/rankings" replace />;
  }

  if (teamId) {
    const route = getFantasyRoute(
      teamId,
      competitionData?.deadlineInfo,
      competitionData?.matches?.length > 0
    );
    console.log('Redirecting to fantasy route:', route, 'weekId:', weekId, 'user:', user);
    return <Navigate to={route} replace />;
  }

  console.log('Redirecting to NewTeam with weekId:', weekId, 'user:', user);
  return <NewTeam weekId={weekId} user={user} />;
};

const EditTeamRoute = ({ weekId, sessionTeamId, competitionData }: any) => {
  const { id } = useParams();
  const teamId = id ?? sessionTeamId;

  if (isGameEnded(competitionData)) {
    return teamId
      ? <Navigate to={`/fantasy/points/${teamId}`} replace />
      : <Navigate to="/fantasy/rankings" replace />;
  }

  return <EditTeam teamId={teamId} weekId={weekId} />;
};

const LayoutWithWeekSwitch = () => {
  const { competitionData } = useCompetitionContext();
  return (
    <>
      <WeekSwitch competitionData={competitionData} />
      <Outlet />
    </>
  );
};

const queryClient = new QueryClient();

// Component to pass the URL param id to TeamProvider
const PublicTeamRoute = ({ weekId, competitionData } : any) => {
  const { id } = useParams();

  return (
    <TeamProvider teamId={id} withPoints={true} competitionData={competitionData} currentWeekId={weekId}>
      <Points weekId={weekId} />
    </TeamProvider>
  );
};

const FantasyRoutes = ({ sessionTeamId, user, competitionData, weekId }: any) => {
  const location = useLocation();
  const routeTeamId = getTeamIdFromPathname(location.pathname);
  const activeTeamId = routeTeamId ?? sessionTeamId;
  const usePointsEndpoint =
    location.pathname.startsWith('/fantasy/points/') ||
    location.pathname.startsWith('/fantasy/public/') ||
    isGameEnded(competitionData);

  return (
    <RouteGuard user={user} teamId={sessionTeamId}>
      <TeamProvider
        teamId={activeTeamId}
        withPoints={usePointsEndpoint}
        competitionData={competitionData}
        currentWeekId={weekId}
      >
        <Routes>
          <Route path="/fantasy" element={<LayoutWithWeekSwitch />}>
            <Route
              path="new"
              element={
                <RedirectIfTeamExists
                  teamId={sessionTeamId}
                  weekId={weekId}
                  user={user}
                  competitionData={competitionData}
                />
              }
            />
            <Route
              path="edit/:id"
              element={
                <EditTeamRoute
                  weekId={weekId}
                  sessionTeamId={sessionTeamId}
                  competitionData={competitionData}
                />
              }
            />
            <Route path="points/:id" element={weekId !== 0 ? <Points weekId={weekId} /> : <LoadingSpinner />} />
            {/* Public route with special handling to use the URL param as teamId */}
            <Route path="public/:id" element={
              weekId !== 0 ? <PublicTeamRoute weekId={weekId} competitionData={competitionData} />
              : <LoadingSpinner />
            } />
              {/* <Route path="team/:id" element={<Team weekId={weekId} />} /> */}
              {/* <Route path="transfers/:id" element={<Transfers weekId={weekId} />} /> */}
            <Route path="leagues" element={<Leagues weekId={weekId} />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="rankings" element={<Ranking />} />
          </Route>
          <Route path="/*" element={<div>404 Not Found</div>} />
        </Routes>
      </TeamProvider>
    </RouteGuard>
  );
};

export default function App() {
  const [isClient, setIsClient] = useState(false);
  const { data: session } = useSession();

  const [teamId, setTeamId] = useState<string | null>(null);
type SessionUser = {
  id: string;
  userId: number | null;
  teamId: string | null;
  competitionFeed: string;
  seasonId: number;
  email?: string;
  name?: string;
};

const [user, setUser] = useState<Partial<SessionUser>>({});
  // Read teamId from cookie on client side
  useEffect(() => {
    setTeamId(session?.user?.teamId || null);
    setUser({
     userId: session?.user?.userId,
     id: session?.user?.id,
    });
}, [session]);

  const { competitionData, weekId } = useCompetitionContext();

  useEffect(() => {
    if (!user) return; // Wait for session data
    const interval = setInterval(() => {
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        window.$crisp.push(["set", "user:email", [user?.email]]);
        window.$crisp.push(["set", "user:nickname", [user?.name]]);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    setIsClient(true);
  }, []);


  return (
    <>
      {isClient && (

              <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                  <FantasyRoutes
                    sessionTeamId={teamId}
                    user={user}
                    competitionData={competitionData}
                    weekId={weekId}
                  />
                </BrowserRouter>
              </QueryClientProvider>

        
      )}
    </>
  );
}
